import { ReactElement, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Grid, Popover } from 'antd';
import { OrdersToolbarFilters } from '@/hooks/useOrdersToolbarFilters';
import FilterMenu, { MenuField } from './FilterMenu';
import { FOCUS_RING } from './toolbarConfig';

/**
 * Where the filter menu opens: an anchored popover (≥ sm) or a bottom sheet
 * (phones). Same FilterMenu inside both.
 */
interface Props {
  filters: OrdersToolbarFilters;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field?: MenuField | null;
  /** Tokens open toward the end side; the Filter button (end of the bar) toward the start. */
  placement?: 'bottomLeft' | 'bottomRight';
  children: ReactElement;
}

const FilterSurface = ({
  filters,
  open,
  onOpenChange,
  field,
  placement = 'bottomRight',
  children,
}: Props) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const close = () => onOpenChange(false);
  const menu = open ? <FilterMenu filters={filters} initialField={field} onClose={close} /> : null;

  if (screens.sm === false) {
    return (
      <>
        {cloneElement(children, { onClick: () => onOpenChange(true) })}
        <Drawer
          open={open}
          onClose={close}
          placement='bottom'
          height='auto'
          title={String(t('filter'))}
          rootClassName='orders-filter-sheet'
          footer={
            <button
              type='button'
              onClick={close}
              className={`w-full h-10 rounded-lg border-0 bg-sd-primary text-sd-primary-foreground text-[13px] font-semibold cursor-pointer ${FOCUS_RING}`}
            >
              {String(t('done'))}
            </button>
          }
        >
          {menu}
        </Drawer>
      </>
    );
  }
  return (
    <Popover
      trigger='click'
      placement={placement}
      arrow={false}
      open={open}
      onOpenChange={onOpenChange}
      destroyTooltipOnHide
      rootClassName='orders-filter-popover'
      content={<div className='w-[288px] max-w-[calc(100vw-32px)]'>{menu}</div>}
    >
      {children}
    </Popover>
  );
};

export default FilterSurface;
